import { useEffect, useState } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import SEOSchema from '../../components/SEOSchema';

export default function Pallets48x40Page() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleQuoteClick = () => {
    const contactSection = document.getElementById('contact-form');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new FormData(e.currentTarget);
      
      await fetch('https://readdy.ai/api/form/d5re50otu5df1co4dhhg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      setSubmitStatus('success');
      (e.target as HTMLFormElement).reset();
      
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOSchema type="product" />
      <Navigation isScrolled={isScrolled} />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[600px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 w-full h-full">
            <img
              src="https://public.readdy.ai/ai/img_res/d2e1770a1b729b8994c1852b3bc7e67f.jpg"
              alt="Buy 48x40 Pallets Nationwide"
              title="48x40 Standard Pallets"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
          </div>
          
          <div className="relative z-10 max-w-5xl mx-auto px-6 text-center text-white">
            <h1 className="text-6xl lg:text-7xl font-bold mb-6">
              Buy 48×40 Pallets Nationwide
            </h1>
            <p className="text-xl lg:text-2xl mb-8 text-white/90 max-w-3xl mx-auto">
              Industry-standard 48×40 pallets delivered across all 50 states. Premium quality, competitive pricing, and fast turnaround times guaranteed.
            </p>
            <button
              onClick={handleQuoteClick}
              className="bg-orange-600 hover:bg-orange-700 text-white px-10 py-4 rounded-full text-lg font-semibold transition-all transform hover:scale-105 shadow-xl whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </button>
          </div>
        </section>

        {/* Product Overview */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <p className="text-orange-600 text-sm font-bold mb-3 flex items-center">
                  <span className="w-2 h-2 bg-orange-600 rounded-full mr-2"></span>
                  INDUSTRY STANDARD
                </p>
                <h2 className="text-5xl font-bold text-gray-900 mb-6">
                  Why Choose 48×40 Pallets?
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  The <strong>48×40 pallet</strong> is the most widely used pallet size in North America, making it the perfect choice for businesses that need compatibility with standard warehouse equipment, racking systems, and transportation methods.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  When you <strong>Buy 48×40 Pallets Nationwide</strong> from us, you're getting pallets that meet or exceed GMA specifications, ensuring consistent quality and performance across your entire supply chain.
                </p>
              </div>
              
              <div className="w-full h-[500px]">
                <img
                  src="https://readdy.ai/api/search-image?query=Close-up%20view%20of%20high-quality%20wooden%2048x40%20pallet%20showing%20construction%20details%2C%20sturdy%20deck%20boards%2C%20professional%20craftsmanship%2C%20clean%20simple%20background%2C%20product%20photography%20style%2C%20well-lit%20studio%20setting&width=600&height=500&seq=48x40detail&orientation=portrait"
                  alt="48x40 Pallet Construction Details"
                  title="Quality 48x40 Pallet"
                  className="w-full h-full object-cover object-center rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Grades Available */}
        <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-4">
                Available Grades
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Choose the perfect grade for your application and budget
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-product-shop>
              <article className="bg-white rounded-2xl p-10 shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-2">
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-star-line text-4xl text-teal-600"></i>
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">#1 Grade Pallets</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Premium quality pallets with minimal wear. Perfect for retail display, export shipping, and applications requiring the highest standards. These pallets are carefully inspected and meet strict quality criteria.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Minimal to no repairs</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Clean appearance</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Maximum load capacity</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Export-ready</span>
                  </li>
                </ul>
                <button
                  onClick={handleQuoteClick}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white py-4 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer"
                >
                  Request Quote
                </button>
              </article>

              <article className="bg-white rounded-2xl p-10 shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-2">
                <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-stack-line text-4xl text-orange-600"></i>
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">#2 Grade Pallets</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Excellent value for warehousing and general shipping needs. These pallets are structurally sound with minor cosmetic imperfections, offering great performance at a lower cost.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <i className="ri-check-line text-orange-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Structurally sound</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-orange-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Cost-effective solution</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-orange-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Suitable for most applications</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-orange-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Reliable performance</span>
                  </li>
                </ul>
                <button
                  onClick={handleQuoteClick}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white py-4 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer"
                >
                  Request Quote
                </button>
              </article>
            </div>
          </div>
        </section>

        {/* Specifications */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-4">
                Technical Specifications
              </h2>
              <p className="text-xl text-gray-600">
                Detailed specifications for our 48×40 pallets
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-12 shadow-xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <i className="ri-ruler-line text-orange-600 text-3xl mr-3"></i>
                    Dimensions
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-3 border-b border-gray-200">
                      <span className="text-gray-700 font-semibold">Length</span>
                      <span className="text-gray-900 font-bold">48 inches</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-gray-200">
                      <span className="text-gray-700 font-semibold">Width</span>
                      <span className="text-gray-900 font-bold">40 inches</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-gray-200">
                      <span className="text-gray-700 font-semibold">Height</span>
                      <span className="text-gray-900 font-bold">5.5 inches</span>
                    </div>
                    <div className="flex justify-between items-center py-3">
                      <span className="text-gray-700 font-semibold">Weight</span>
                      <span className="text-gray-900 font-bold">40-50 lbs</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <i className="ri-settings-3-line text-teal-600 text-3xl mr-3"></i>
                    Construction
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-3 border-b border-gray-200">
                      <span className="text-gray-700 font-semibold">Deck Boards</span>
                      <span className="text-gray-900 font-bold">7 boards</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-gray-200">
                      <span className="text-gray-700 font-semibold">Bottom Boards</span>
                      <span className="text-gray-900 font-bold">5 boards</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-gray-200">
                      <span className="text-gray-700 font-semibold">Stringers</span>
                      <span className="text-gray-900 font-bold">3 stringers</span>
                    </div>
                    <div className="flex justify-between items-center py-3">
                      <span className="text-gray-700 font-semibold">Entry</span>
                      <span className="text-gray-900 font-bold">4-way</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-12 pt-12 border-t border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <i className="ri-shield-check-line text-lime-600 text-3xl mr-3"></i>
                  Load Capacity
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white rounded-xl p-6 shadow-md">
                    <p className="text-gray-600 text-sm mb-2">Static Load</p>
                    <p className="text-3xl font-bold text-gray-900">5,500 lbs</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-md">
                    <p className="text-gray-600 text-sm mb-2">Dynamic Load</p>
                    <p className="text-3xl font-bold text-gray-900">2,500 lbs</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-md">
                    <p className="text-gray-600 text-sm mb-2">Racking Load</p>
                    <p className="text-3xl font-bold text-gray-900">2,000 lbs</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-24 bg-gradient-to-br from-teal-50 to-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-4">
                Why Buy From Us?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Experience the difference when you <strong>Buy 48×40 Pallets Nationwide</strong> from a trusted supplier
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-truck-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Nationwide Delivery</h3>
                <p className="text-gray-600 leading-relaxed">
                  Fast shipping to all 50 states with reliable logistics partners and tracking available for every order.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-price-tag-3-line text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Competitive Pricing</h3>
                <p className="text-gray-600 leading-relaxed">
                  Volume discounts available with transparent pricing and no hidden fees. Get the best value for your investment.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="w-16 h-16 bg-lime-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-shield-check-line text-3xl text-lime-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Quality Guaranteed</h3>
                <p className="text-gray-600 leading-relaxed">
                  Every pallet is inspected to ensure it meets our strict quality standards before shipping to your location.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-time-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Fast Turnaround</h3>
                <p className="text-gray-600 leading-relaxed">
                  Large inventory means quick fulfillment. Most orders ship within 24-48 hours of confirmation.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-customer-service-2-line text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Expert Support</h3>
                <p className="text-gray-600 leading-relaxed">
                  Our team is ready to help you choose the right pallets and answer any questions about your order.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="w-16 h-16 bg-lime-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-recycle-line text-3xl text-lime-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Eco-Friendly Options</h3>
                <p className="text-gray-600 leading-relaxed">
                  Recycled and reconditioned pallets available to support your sustainability goals and reduce costs.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section id="contact-form" className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-5xl font-bold text-gray-900 mb-4">
                Get Your Free Quote
              </h2>
              <p className="text-xl text-gray-600">
                Fill out the form below and we'll get back to you within 24 hours
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-10 shadow-2xl">
              <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="john@company.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Your Company"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="grade" className="block text-sm font-semibold text-gray-700 mb-2">
                      Pallet Grade *
                    </label>
                    <select
                      id="grade"
                      name="grade"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="">Select Grade</option>
                      <option value="grade1">#1 Grade</option>
                      <option value="grade2">#2 Grade</option>
                      <option value="both">Both Grades</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="quantity" className="block text-sm font-semibold text-gray-700 mb-2">
                      Quantity Needed *
                    </label>
                    <input
                      type="number"
                      id="quantity"
                      name="quantity"
                      required
                      min="1"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="100"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="delivery" className="block text-sm font-semibold text-gray-700 mb-2">
                    Delivery Location *
                  </label>
                  <input
                    type="text"
                    id="delivery"
                    name="delivery"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="City, State"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Additional Details
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                    placeholder="Tell us about your specific requirements..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white py-4 rounded-full text-lg font-semibold transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-center">
                    Thank you! We'll get back to you within 24 hours.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-center">
                    Something went wrong. Please try again.
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}